import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';

interface ApiResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T;
}

interface ApiListResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T[];
  total: number;
}

// ── Push devices ────────────────────────────────────────────────────────────

export type PushPlatform = 'ios' | 'android' | 'web';
export type PushProvider = 'fcm' | 'apns';

export interface PushDeviceIn {
  device_token: string;
  platform: PushPlatform;
  provider: PushProvider;
}

export interface PushDeviceOut {
  id: string;
  device_token: string;
  platform: PushPlatform;
  provider: PushProvider;
  is_active: boolean;
  last_registered_on: string;
  created_on: string;
}

// ── In-app inbox ─────────────────────────────────────────────────────────────

export type Classification = 'critical' | 'important' | 'info';
export type ActionStatus =
  | 'Pending'
  | 'Completed'
  | 'Expired'
  | 'Cancelled'
  | 'NotApplicable';

export interface InAppNotificationOut {
  id: string;
  notification_request_id: string;
  title: string;
  message: string;
  classification: Classification;
  source_module?: string;
  source_entity_type?: string;
  source_entity_id?: string;
  action_type?: string;
  action_route?: string;
  is_system_critical: boolean;
  is_action_required: boolean;
  action_status: ActionStatus;
  read: boolean;
  archived: boolean;
  created_on: string;
  read_on?: string;
  expiry_on?: string;
}

export interface InboxCountsOut {
  unread: number;
  action_required: number;
  total: number;
}

export interface GetInboxParams {
  read?: boolean;
  action_required?: boolean;
  classification?: Classification;
  archived?: boolean;
  limit?: number;
  skip?: number;
}

export interface InboxPageResult {
  items: InAppNotificationOut[];
  total: number;
}

export interface ActionValidateOut {
  action_route: string;
  action_type: string;
  source_module?: string;
  source_entity_type?: string;
  source_entity_id?: string;
}

// ── API slice ────────────────────────────────────────────────────────────────

export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['PushDevices', 'Inbox', 'InboxCounts'],
  endpoints: (builder) => ({
    // Push devices
    registerDevice: builder.mutation<PushDeviceOut, PushDeviceIn>({
      query: (body) => ({ url: '/api/v1/notifications/devices', method: 'POST', body }),
      transformResponse: (res: ApiResponse<PushDeviceOut>) => res.data,
      invalidatesTags: ['PushDevices'],
    }),
    unregisterDevice: builder.mutation<void, string>({
      query: (deviceId) => ({
        url: `/api/v1/notifications/devices/${deviceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PushDevices'],
    }),
    getDevices: builder.query<PushDeviceOut[], void>({
      query: () => '/api/v1/notifications/devices',
      transformResponse: (res: ApiResponse<PushDeviceOut[]>) => res.data,
      providesTags: ['PushDevices'],
    }),

    // Inbox
    getInbox: builder.query<InboxPageResult, GetInboxParams>({
      query: (params) => ({ url: '/api/v1/notifications/inbox', params }),
      transformResponse: (res: ApiListResponse<InAppNotificationOut>) => ({
        items: res.data,
        total: res.total,
      }),
      providesTags: (result) => [
        'Inbox',
        ...(result?.items ?? []).map((n) => ({ type: 'Inbox' as const, id: n.id })),
      ],
    }),
    getInboxCounts: builder.query<InboxCountsOut, void>({
      query: () => '/api/v1/notifications/inbox/counts',
      transformResponse: (res: ApiResponse<InboxCountsOut>) => res.data,
      providesTags: ['InboxCounts'],
    }),
    markRead: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/v1/notifications/inbox/${id}/read`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Inbox', 'InboxCounts'],
    }),
    markAllRead: builder.mutation<{ updated: number }, void>({
      query: () => ({ url: '/api/v1/notifications/inbox/mark-all-read', method: 'POST' }),
      transformResponse: (res: ApiResponse<{ updated: number }>) => res.data,
      invalidatesTags: ['Inbox', 'InboxCounts'],
    }),
    archiveNotification: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/v1/notifications/inbox/${id}/archive`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Inbox', 'InboxCounts'],
    }),
    unarchiveNotification: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/v1/notifications/inbox/${id}/unarchive`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Inbox', 'InboxCounts'],
    }),
    validateAction: builder.mutation<ActionValidateOut, string>({
      query: (id) => ({
        url: `/api/v1/notifications/inbox/${id}/action-validate`,
        method: 'POST',
      }),
      transformResponse: (res: ApiResponse<ActionValidateOut>) => res.data,
    }),
  }),
});

export const {
  useRegisterDeviceMutation,
  useUnregisterDeviceMutation,
  useGetDevicesQuery,
  useGetInboxQuery,
  useGetInboxCountsQuery,
  useMarkReadMutation,
  useMarkAllReadMutation,
  useArchiveNotificationMutation,
  useUnarchiveNotificationMutation,
  useValidateActionMutation,
} = notificationsApi;
